/**
 * AssetLedger Pro — App Layout
 *
 * Professional sidebar layout with:
 * - Collapsible sidebar with nested navigation
 * - Role-based menu items
 * - Module-aware nav (only shows enabled features)
 * - Breadcrumb header
 * - Notification bell
 * - User avatar dropdown
 * - Session timeout warning
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Package, Calculator, Trash2,
  QrCode, BarChart3, Settings, Users, Bell, ChevronDown,
  ChevronRight, LogOut, User, Shield, Construction, Briefcase,
  ClipboardList, FileText, Menu, X, AlertTriangle, Cpu, Info, Flag,
  PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { useClientSettings } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

// ── Navigation Config ────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  href?: string;
  icon: React.ReactNode;
  roles?: string[];
  feature?: string;           // Required client feature toggle
  children?: NavItem[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: <LayoutDashboard className="w-4 h-4" />,
  },
  {
    label: 'Assets',
    icon: <Package className="w-4 h-4" />,
    children: [
      { label: 'Asset Register', href: '/assets', icon: <Package className="w-4 h-4" /> },
      { label: 'Add Asset', href: '/assets/new', icon: <Package className="w-4 h-4" /> },
      { label: 'Import from Excel', href: '/assets/import', icon: <Package className="w-4 h-4" /> },
      { label: 'Import Sales from Excel', href: '/disposals/import', icon: <Trash2 className="w-4 h-4" /> },
      { label: 'Block of Assets', href: '/blocks', icon: <Briefcase className="w-4 h-4" /> },
      { label: 'Categories', href: '/categories', icon: <ClipboardList className="w-4 h-4" /> },
      { label: 'CWIP', href: '/cwip', icon: <Construction className="w-4 h-4" />, feature: 'enableCWIP' },
      { label: 'Deleted Assets', href: '/assets/deleted', icon: <Trash2 className="w-4 h-4" /> },
    ],
  },
  {
    label: 'Depreciation',
    icon: <Calculator className="w-4 h-4" />,
    children: [
      { label: 'Run Depreciation', href: '/depreciation/run', icon: <Calculator className="w-4 h-4" /> },
      { label: 'Depreciation History', href: '/depreciation/history', icon: <Calculator className="w-4 h-4" /> },
      { label: 'Background Jobs', href: '/jobs', icon: <Cpu className="w-4 h-4" /> },
    ],
  },
  {
    label: 'Disposals',
    icon: <Trash2 className="w-4 h-4" />,
    href: '/disposals',
  },
  {
    label: 'Approvals',
    icon: <Shield className="w-4 h-4" />,
    href: '/approvals',
    feature: 'approvalWorkflowEnabled',
    roles: ['CLIENT_ADMIN'],
  },
  {
    label: 'Physical Verification',
    icon: <QrCode className="w-4 h-4" />,
    children: [
      { label: 'Verification Sessions', href: '/verification', icon: <QrCode className="w-4 h-4" /> },
      { label: 'Print QR Labels', href: '/verification/print', icon: <QrCode className="w-4 h-4" /> },
    ],
  },
  {
    label: 'Renewals & Tracking',
    icon: <Shield className="w-4 h-4" />,
    href: '/insurance',
    feature: 'enableInsuranceAMC',
  },
  {
    label: 'Reports',
    icon: <BarChart3 className="w-4 h-4" />,
    href: '/reports',
  },
  {
    label: 'Users',
    icon: <Users className="w-4 h-4" />,
    href: '/users',
    roles: ['CLIENT_ADMIN'],
  },
  {
    label: 'Branches',
    icon: <Building2 className="w-4 h-4" />,
    href: '/branches',
    feature: 'enableBranchHierarchy',
    roles: ['CLIENT_ADMIN'],
  },
  {
    label: 'Settings',
    icon: <Settings className="w-4 h-4" />,
    href: '/settings',
    roles: ['CLIENT_ADMIN'],
  },
  {
    label: 'Go-Live Migration',
    icon: <Flag className="w-4 h-4" />,
    href: '/migration',
    roles: ['CLIENT_ADMIN'],
  },
];

const SUPER_ADMIN_NAV: NavItem[] = [
  { label: 'Platform Overview', href: '/admin', icon: <LayoutDashboard className="w-4 h-4" /> },
  { label: 'Clients', href: '/admin/clients', icon: <Building2 className="w-4 h-4" /> },
  { label: 'Users', href: '/admin/users', icon: <Users className="w-4 h-4" /> },
  { label: 'Audit Logs', href: '/admin/audit-logs', icon: <ClipboardList className="w-4 h-4" /> },
  { label: 'Purge Deleted Data', href: '/admin/purge', icon: <Trash2 className="w-4 h-4" /> },
  { label: 'Settings', href: '/admin/settings', icon: <Settings className="w-4 h-4" /> },
];

// ── Sidebar NavItem Component ────────────────────────────────────────────────

function SidebarItem({
  item,
  depth = 0,
  onNavigate,
  isOpen,
  onToggle,
  collapsed = false,
  onExpandSidebar,
}: {
  item: NavItem;
  depth?: number;
  onNavigate: () => void;
  isOpen?: boolean;
  onToggle?: () => void;
  collapsed?: boolean;
  onExpandSidebar?: () => void;
}) {
  const location = useLocation();

  const isActive = item.href
    ? location.pathname === item.href ||
      (item.href !== '/dashboard' && location.pathname.startsWith(item.href))
    : false;

  const hasChildren = !!item.children?.length;

  if (hasChildren) {
    const expanded = isOpen ?? false;
    return (
      <div>
        <button
          // In collapsed mode a section click expands the whole sidebar and opens the section
          onClick={() => { if (collapsed) onExpandSidebar?.(); onToggle?.(); }}
          title={collapsed ? item.label : undefined}
          className={cn(
            'w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-colors',
            'text-white/70 hover:text-white hover:bg-white/10',
            collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
            !collapsed && depth > 0 && 'pl-9'
          )}
        >
          <span className="shrink-0">{item.icon}</span>
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{item.label}</span>
              {expanded
                ? <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                : <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
            </>
          )}
        </button>
        {expanded && !collapsed && (
          <div className="mt-0.5 space-y-0.5">
            {item.children!.map((child) => (
              <SidebarItem
                key={child.label}
                item={child}
                depth={depth + 1}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.href!}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={cn(
        'flex items-center gap-3 rounded-lg text-sm font-medium transition-colors',
        collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
        !collapsed && depth > 0 ? 'pl-9' : '',
        isActive
          ? 'bg-white/15 text-white'
          : 'text-white/70 hover:text-white hover:bg-white/10'
      )}
    >
      <span className="shrink-0">{item.icon}</span>
      {!collapsed && <span>{item.label}</span>}
    </Link>
  );
}

// ── Main Layout ──────────────────────────────────────────────────────────────

export default function AppLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('alp-sidebar-collapsed') === '1');
  const [openSection, setOpenSection] = useState<string | null>(null); // only one dropdown open at a time
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  // Persist desktop collapse preference
  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('alp-sidebar-collapsed', next ? '1' : '0');
      return next;
    });
  }, []);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const { data: clientSettings } = useClientSettings();

  // Filter nav items based on feature flags from client settings and user role
  const filterByFeature = (items: NavItem[]): NavItem[] => {
    return items
      .filter(item => {
        if (item.feature && (clientSettings as any)?.[item.feature] !== true) return false;
        if (item.roles && !item.roles.includes(user?.role ?? '')) return false;
        return true;
      })
      .map(item => ({
        ...item,
        children: item.children ? filterByFeature(item.children) : undefined,
      }));
  };

  const navItems = isSuperAdmin ? SUPER_ADMIN_NAV : filterByFeature(NAV_ITEMS);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignore */ }
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // Session timeout warning (show warning 2 minutes before expiry)
  useEffect(() => {
    const checkSession = setInterval(() => {
      const { lastActivityAt } = useAuthStore.getState();
      if (!lastActivityAt) return;
      const idleMs = Date.now() - lastActivityAt;
      const warningMs = (15 - 2) * 60 * 1000; // Show warning 2 mins before 15-min timeout
      setShowSessionWarning(idleMs > warningMs);
    }, 30_000); // Check every 30 seconds

    return () => clearInterval(checkSession);
  }, []);

  const renderSidebar = (isCollapsed: boolean) => (
    <div className="flex flex-col h-full bg-navy">
      {/* Logo — company name takes primary slot, product name sits below.
          A collapse toggle sits on the right (desktop only). */}
      <div className={cn('flex items-center gap-3 border-b border-white/10', isCollapsed ? 'px-3 py-5 justify-center' : 'px-4 py-5')}>
        <div className="w-9 h-9 bg-white/15 rounded-xl flex items-center justify-center shrink-0">
          <span className="text-lg">📊</span>
        </div>
        {!isCollapsed && (
          <>
            <div className="min-w-0 flex-1">
              {user?.client?.name ? (
                <>
                  <div
                    className="text-white font-bold text-sm leading-tight line-clamp-2"
                    title={user.client.name}
                  >
                    {user.client.name}
                  </div>
                  <div className="text-white/60 text-[11px] leading-tight mt-0.5">
                    AssetLedger Pro
                  </div>
                </>
              ) : (
                <div className="text-white font-bold text-sm leading-tight">AssetLedger Pro</div>
              )}
            </div>
            {/* Desktop collapse toggle */}
            <button
              onClick={toggleCollapsed}
              title="Collapse sidebar"
              className="hidden lg:flex shrink-0 text-white/50 hover:text-white p-1 rounded-md hover:bg-white/10"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Expand toggle when collapsed (desktop) */}
      {isCollapsed && (
        <button
          onClick={toggleCollapsed}
          title="Expand sidebar"
          className="hidden lg:flex items-center justify-center py-2 text-white/50 hover:text-white hover:bg-white/10 border-b border-white/10"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      )}

      {/* Navigation */}
      <nav className={cn('flex-1 overflow-y-auto space-y-0.5', isCollapsed ? 'p-2' : 'p-3')}>
        {navItems.map((item) => (
          <SidebarItem
            key={item.label}
            item={item}
            collapsed={isCollapsed}
            onExpandSidebar={() => { setCollapsed(false); localStorage.setItem('alp-sidebar-collapsed', '0'); }}
            onNavigate={() => setSidebarOpen(false)}
            isOpen={openSection === item.label}
            onToggle={() => setOpenSection(openSection === item.label ? null : item.label)}
          />
        ))}
      </nav>

      {/* About */}
      <div className={cn('pb-1', isCollapsed ? 'px-2' : 'px-3')}>
        <button
          onClick={() => { if (isCollapsed) { setCollapsed(false); localStorage.setItem('alp-sidebar-collapsed', '0'); } setOpenSection(openSection === '__about' ? null : '__about'); }}
          title={isCollapsed ? 'About' : undefined}
          className={cn('w-full flex items-center rounded-lg text-sm font-medium text-white/50 hover:text-white hover:bg-white/10 transition-colors',
            isCollapsed ? 'justify-center py-2' : 'gap-2.5 px-3 py-2')}
        >
          <Info className="w-4 h-4 shrink-0" />
          {!isCollapsed && <span>About</span>}
        </button>
        {openSection === '__about' && !isCollapsed && (
          <div className="mx-2 mt-1 mb-2 p-3 bg-white/5 rounded-lg border border-white/10 text-white/60 text-[11px] leading-relaxed space-y-1.5">
            <p className="text-white/80 font-semibold text-xs">AssetLedger Pro <span className="font-normal text-white/50">v{__APP_VERSION__}</span></p>
            <p>Professional Fixed Asset Register</p>
            <p className="pt-1 border-t border-white/10">Developed by <span className="text-white/80 font-medium">Authentic Technologies</span></p>
            <p><a href="mailto:autech1402@gmail.com" className="text-white/50 hover:text-white/80">autech1402@gmail.com</a></p>
            <p>&copy; 2026 All rights reserved.</p>
            <p className="pt-1 border-t border-white/10 text-[10px]">By using this software, you accept the End User License Agreement (EULA).</p>
          </div>
        )}
      </div>

      {/* User Info */}
      <div className={cn('border-t border-white/10', isCollapsed ? 'p-2' : 'p-3')}>
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-1">
            <a href="/profile" onClick={e => { e.preventDefault(); navigate('/profile'); setSidebarOpen(false); }}
              title={user?.name} className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center hover:opacity-90">
              <span className="text-white text-xs font-semibold">{user?.name?.charAt(0).toUpperCase()}</span>
            </a>
            <button onClick={handleLogout} title="Sign out" className="text-white/50 hover:text-white p-1.5 rounded-md hover:bg-white/10">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors">
            <a href="/profile" onClick={e => { e.preventDefault(); navigate('/profile'); setSidebarOpen(false); }}
              className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center shrink-0">
                <span className="text-white text-xs font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium truncate">{user?.name}</div>
                <div className="text-white/50 text-xs truncate capitalize">{user?.role?.toLowerCase().replace('_', ' ')}</div>
              </div>
            </a>
            <button onClick={handleLogout} title="Sign out" className="text-white/50 hover:text-white">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop sidebar — collapsible. Inline width (JIT-independent); no width
          transition — animating width on a flex item can wedge the layout. */}
      <aside
        className="hidden lg:flex shrink-0 shadow-sidebar overflow-hidden"
        style={{ width: collapsed ? '4rem' : '16rem' }}
      >
        {renderSidebar(collapsed)}
      </aside>

      {/* Mobile sidebar overlay (always expanded) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-64 animate-slide-in">
            {renderSidebar(false)}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="h-14 bg-white border-b border-border flex items-center px-4 gap-4 shrink-0">
          <button
            className="lg:hidden text-muted-foreground hover:text-foreground"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Company name on the top bar — shown only when it isn't already visible
              in the sidebar (i.e. on mobile where the sidebar is hidden, or on
              desktop when the sidebar is collapsed). Avoids the duplicate name
              sitting right next to the sidebar's own name. */}
          {user?.client?.name && (
            <div className={cn(
              'items-center gap-2 px-3 py-1 rounded-lg bg-muted/40 border border-border',
              collapsed ? 'flex' : 'flex lg:hidden'
            )}>
              <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-sm font-semibold text-foreground truncate max-w-[16rem]" title={user.client.name}>
                {user.client.name}
              </span>
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Notifications */}
          <button className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <Bell className="w-4 h-4" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
          </button>

          {/* User menu — click to go to profile */}
          <a href="/profile" onClick={e => { e.preventDefault(); navigate('/profile'); }}
            className="flex items-center gap-2 cursor-pointer group">
            <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center">
              <span className="text-white text-xs font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="text-sm font-medium text-foreground hidden sm:block">{user?.name}</span>
          </a>
        </header>

        {/* Session warning banner */}
        {showSessionWarning && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800 flex-1">
              Your session will expire soon due to inactivity.
            </p>
            <button
              onClick={() => {
                useAuthStore.getState().updateLastActivity();
                setShowSessionWarning(false);
              }}
              className="text-sm font-medium text-amber-900 hover:text-amber-700 underline"
            >
              Stay signed in
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
